// Система управления источниками медицинских данных
export interface MedicalSource {
  id: string;
  name: string;
  type: 'official' | 'guideline' | 'research' | 'atlas' | 'protocol';
  authority: string;
  country: string;
  lastUpdated: Date;
  url?: string;
  reliability: 'high' | 'medium' | 'low';
  description: string;
  contactInfo?: string;
}

export interface SourceReference {
  sourceId: string;
  page?: string;
  section?: string;
  version?: string;
  lastChecked: Date;
}

// База источников медицинских данных
export const medicalSources: MedicalSource[] = [
  {
    id: 'who-vaccination',
    name: 'ВОЗ - Рекомендации по вакцинации',
    type: 'official',
    authority: 'Всемирная организация здравоохранения',
    country: 'Международный',
    lastUpdated: new Date('2024-01-15'),
    url: 'https://www.who.int/immunization/policy/immunization_tables/en/',
    reliability: 'high',
    description: 'Официальные рекомендации ВОЗ по вакцинации детей',
    contactInfo: 'immunization@who.int'
  },
  {
    id: 'cdc-growth-charts',
    name: 'CDC - Графики роста и веса',
    type: 'official',
    authority: 'Центры по контролю и профилактике заболеваний США',
    country: 'США',
    lastUpdated: new Date('2024-02-01'),
    url: 'https://www.cdc.gov/growthcharts/',
    reliability: 'high',
    description: 'Стандартные графики роста и веса CDC для детей',
    contactInfo: 'cdcinfo@cdc.gov'
  },
  {
    id: 'minzdrav-orders',
    name: 'Приказы Минздрава России',
    type: 'official',
    authority: 'Министерство здравоохранения Российской Федерации',
    country: 'Россия',
    lastUpdated: new Date('2024-03-01'),
    url: 'https://minzdrav.gov.ru',
    reliability: 'high',
    description: 'Официальные приказы и нормативы Минздрава РФ',
    contactInfo: 'info@minzdrav.gov.ru'
  },
  {
    id: 'dermnet-atlas',
    name: 'DermNet NZ - Атлас дерматологии',
    type: 'atlas',
    authority: 'DermNet New Zealand',
    country: 'Новая Зеландия',
    lastUpdated: new Date('2024-01-20'),
    url: 'https://dermnetnz.org',
    reliability: 'high',
    description: 'Международный атлас дерматологических заболеваний',
    contactInfo: 'info@dermnetnz.org'
  },
  {
    id: 'rki-guidelines',
    name: 'RKI - Руководства по инфекционным заболеваниям',
    type: 'guideline',
    authority: 'Институт Роберта Коха',
    country: 'Германия',
    lastUpdated: new Date('2024-02-15'),
    url: 'https://www.rki.de',
    reliability: 'high',
    description: 'Руководства по диагностике и лечению инфекционных заболеваний',
    contactInfo: 'kontakt@rki.de'
  },
  {
    id: 'who-growth-standards',
    name: 'ВОЗ - Стандарты роста детей',
    type: 'official',
    authority: 'Всемирная организация здравоохранения',
    country: 'Международный',
    lastUpdated: new Date('2024-01-10'),
    url: 'https://www.who.int/tools/child-growth-standards',
    reliability: 'high',
    description: 'Международные стандарты роста и развития детей',
    contactInfo: 'growth@who.int'
  },
  {
    id: 'pediatric-formulary',
    name: 'Справочник педиатрических дозировок',
    type: 'protocol',
    authority: 'Российская педиатрическая ассоциация',
    country: 'Россия',
    lastUpdated: new Date('2024-02-20'),
    reliability: 'high',
    description: 'Официальный справочник дозировок лекарственных препаратов для детей',
    contactInfo: 'info@pediatr-russia.ru'
  }
];

// Функции для работы с источниками
export const sourceManager = {
  // Получить источник по ID
  getSource: (id: string): MedicalSource | undefined => {
    return medicalSources.find(source => source.id === id);
  },

  // Получить все источники определенного типа
  getSourcesByType: (type: MedicalSource['type']): MedicalSource[] => {
    return medicalSources.filter(source => source.type === type);
  },

  // Получить источники по надежности
  getSourcesByReliability: (reliability: MedicalSource['reliability']): MedicalSource[] => {
    return medicalSources.filter(source => source.reliability === reliability);
  },

  // Получить источники по стране
  getSourcesByCountry: (country: string): MedicalSource[] => {
    return medicalSources.filter(source => source.country === country);
  },

  // Получить все источники
  getAllSources: (): MedicalSource[] => {
    return medicalSources;
  },

  // Создать ссылку на источник
  createReference: (sourceId: string, page?: string, section?: string, version?: string): SourceReference => {
    return {
      sourceId,
      page,
      section,
      version,
      lastChecked: new Date()
    };
  },

  // Получить информацию о источнике для отображения
  getSourceInfo: (sourceId: string): string => {
    const source = sourceManager.getSource(sourceId);
    if (!source) return 'Источник не найден';
    
    return `${source.name} (${source.authority}, ${source.country})`;
  },

  // Получить цвет для отображения надежности
  getReliabilityColor: (reliability: MedicalSource['reliability']): string => {
    switch (reliability) {
      case 'high': return '#4CAF50';
      case 'medium': return '#FF9800';
      case 'low': return '#F44336';
      default: return '#9E9E9E';
    }
  },

  // Получить иконку для типа источника
  getTypeIcon: (type: MedicalSource['type']): string => {
    switch (type) {
      case 'official': return '🏛️';
      case 'guideline': return '📋';
      case 'research': return '🔬';
      case 'atlas': return '📖';
      case 'protocol': return '📄';
      default: return '📚';
    }
  },

  // Проверить актуальность источника
  isSourceUpToDate: (sourceId: string, maxAgeMonths: number = 12): boolean => {
    const source = sourceManager.getSource(sourceId);
    if (!source) return false;
    
    const now = new Date();
    const sourceDate = source.lastUpdated;
    const diffMonths = (now.getFullYear() - sourceDate.getFullYear()) * 12 + 
                      (now.getMonth() - sourceDate.getMonth());
    
    return diffMonths <= maxAgeMonths;
  },

  // Получить статистику источников
  getSourcesStats: () => {
    const stats = {
      total: medicalSources.length,
      byType: {} as Record<MedicalSource['type'], number>,
      byReliability: {} as Record<MedicalSource['reliability'], number>,
      byCountry: {} as Record<string, number>,
      outdated: 0
    };

    medicalSources.forEach(source => {
      // По типам
      stats.byType[source.type] = (stats.byType[source.type] || 0) + 1;
      
      // По надежности
      stats.byReliability[source.reliability] = (stats.byReliability[source.reliability] || 0) + 1;
      
      // По странам
      stats.byCountry[source.country] = (stats.byCountry[source.country] || 0) + 1;
      
      // Устаревшие
      if (!sourceManager.isSourceUpToDate(source.id)) {
        stats.outdated++;
      }
    });

    return stats;
  }
};

// Экспорт для использования в компонентах
export default sourceManager;
